import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, UserPlus, CheckCircle, Link2, BarChart2, Banknote, Infinity } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BENEFITS = [
  { icon: Banknote,  text: 'Gana comisiones por cada venta realizada' },
  { icon: Link2,     text: 'Link único personalizado para compartir' },
  { icon: BarChart2, text: 'Panel de control con estadísticas en tiempo real' },
  { icon: CheckCircle, text: 'Retiros automáticos a tu cuenta bancaria' },
  { icon: Infinity,  text: 'Sin límite de ganancias' },
];

const RegistroVendedor = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    cedula: '',
    celular: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    if (formData.password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/registro-vendedor`, {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        cedula: formData.cedula,
        celular: formData.celular
      });

      toast.success('¡Registro exitoso! Bienvenido como vendedor');
      setTimeout(() => navigate('/vendedor-dashboard'), 1000);
    } catch (error) {
      console.error('Error en registro:', error);
      toast.error(error.response?.data?.detail || 'Error al registrarse como vendedor');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 relative overflow-hidden flex flex-col">

      {/* ── Ambient glow orbs ── */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-green-500/5 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-orange-600/5 blur-[100px] rounded-full pointer-events-none" />

      {/* ── Back button ── */}
      <div className="relative z-10 max-w-5xl mx-auto w-full px-4 pt-6">
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-slate-400 hover:text-white transition-colors border border-white/10 bg-white/5 backdrop-blur-sm px-4 py-2.5 rounded-xl hover:border-white/20"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Volver al Inicio
        </button>
      </div>

      {/* ── Main layout ── */}
      <div className="relative z-10 flex-1 flex items-start justify-center px-4 py-10">
        <div className="w-full max-w-5xl grid lg:grid-cols-2 gap-10 items-start">

          {/* ════════════════════════════════════════════
              LEFT — Form
          ════════════════════════════════════════════ */}
          <div className="relative">
            {/* Card glow border */}
            <div className="absolute -inset-px rounded-3xl bg-gradient-to-br from-green-500/20 via-transparent to-orange-500/10 opacity-60" />
            <div className="relative rounded-3xl bg-slate-900/80 backdrop-blur-xl border border-white/8 overflow-hidden">

              {/* Card header */}
              <div className="px-8 pt-8 pb-6 border-b border-white/5">
                <div className="flex items-center justify-center gap-3 mb-1">
                  <div className="w-10 h-10 rounded-2xl bg-green-500/15 border border-green-500/25 flex items-center justify-center">
                    <UserPlus className="w-5 h-5 text-green-400" />
                  </div>
                  <h1 className="text-2xl font-black text-white uppercase tracking-tight italic">
                    Conviértete en Vendedor
                  </h1>
                </div>
                <p className="text-center text-slate-400 text-sm font-medium mt-2">
                  Gana comisiones vendiendo sorteos de WishWay
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="p-8 space-y-5">

                {/* 2-col mini grid for cedula + celular */}
                <div className="space-y-1">
                  <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Nombre Completo</Label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Ej: Juan Pérez"
                    className="h-12 bg-white/5 border-white/10 text-white placeholder:text-white/25 rounded-xl focus:border-green-500/50 focus:ring-green-500/20 transition-colors font-medium"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Cédula</Label>
                    <Input
                      type="text"
                      name="cedula"
                      value={formData.cedula}
                      onChange={handleChange}
                      required
                      placeholder="1234567890"
                      className="h-12 bg-white/5 border-white/10 text-white placeholder:text-white/25 rounded-xl focus:border-green-500/50 focus:ring-green-500/20 transition-colors font-medium"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Celular</Label>
                    <Input
                      type="tel"
                      name="celular"
                      value={formData.celular}
                      onChange={handleChange}
                      required
                      placeholder="0999999999"
                      className="h-12 bg-white/5 border-white/10 text-white placeholder:text-white/25 rounded-xl focus:border-green-500/50 focus:ring-green-500/20 transition-colors font-medium"
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Correo Electrónico</Label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="tu@email.com"
                    className="h-12 bg-white/5 border-white/10 text-white placeholder:text-white/25 rounded-xl focus:border-green-500/50 focus:ring-green-500/20 transition-colors font-medium"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Contraseña</Label>
                    <Input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      minLength={6}
                      placeholder="Mín. 6 caracteres"
                      className="h-12 bg-white/5 border-white/10 text-white placeholder:text-white/25 rounded-xl focus:border-green-500/50 focus:ring-green-500/20 transition-colors font-medium"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Confirmar</Label>
                    <Input
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required
                      minLength={6}
                      placeholder="Repite tu contraseña"
                      className="h-12 bg-white/5 border-white/10 text-white placeholder:text-white/25 rounded-xl focus:border-green-500/50 focus:ring-green-500/20 transition-colors font-medium"
                    />
                  </div>
                </div>

                {/* CTA */}
                <button
                  type="submit"
                  disabled={loading}
                  className="relative w-full h-14 mt-2 rounded-2xl font-black text-base uppercase tracking-wider text-black overflow-hidden transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60 disabled:pointer-events-none"
                  style={{
                    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                    boxShadow: '0 0 40px rgba(34,197,94,0.3)',
                  }}
                >
                  {/* Shine sweep */}
                  {!loading && (
                    <span className="shine-overlay" />
                  )}
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    {loading ? (
                      <>
                        <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                        Registrando...
                      </>
                    ) : (
                      <>
                        <UserPlus className="w-5 h-5" />
                        Registrarme como Vendedor
                      </>
                    )}
                  </span>
                </button>

                <p className="text-center text-slate-500 text-sm font-medium">
                  ¿Ya tienes cuenta?{' '}
                  <Link to="/login" className="text-green-400 font-black hover:text-green-300 transition-colors">
                    Inicia Sesión
                  </Link>
                </p>
              </form>
            </div>
          </div>

          {/* ════════════════════════════════════════════
              RIGHT — Benefits + heading
          ════════════════════════════════════════════ */}
          <div className="space-y-8 lg:pt-4">

            {/* Heading */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-px w-8 bg-green-500/40" />
                <span className="text-[10px] font-black uppercase tracking-[0.3em] text-green-500">
                  Programa de Afiliados
                </span>
              </div>
              <h2 className="text-4xl lg:text-5xl font-black text-white tracking-tighter uppercase italic leading-none mb-4">
                Gana dinero<br />
                <span className="text-green-400 [text-shadow:0_0_30px_rgba(74,222,128,0.4)]">
                  desde el primer día
                </span>
              </h2>
              <p className="text-slate-400 leading-relaxed font-medium text-sm">
                Comparte tu link exclusivo, y por cada boleto que se venda a través de él recibirás una comisión directo a tu balance.
              </p>
            </div>

            {/* Benefit pills */}
            <div className="space-y-3">
              {BENEFITS.map(({ icon: Icon, text }) => (
                <div
                  key={text}
                  className="group flex items-center gap-4 p-4 rounded-2xl border border-white/5 bg-white/[0.03] backdrop-blur-sm hover:border-green-500/20 hover:bg-green-500/5 transition-all duration-300"
                >
                  <div className="w-9 h-9 rounded-xl bg-green-500/10 border border-green-500/20 flex items-center justify-center shrink-0 group-hover:bg-green-500/20 transition-colors">
                    <Icon className="w-4 h-4 text-green-400" />
                  </div>
                  <span className="text-slate-300 text-sm font-semibold group-hover:text-white transition-colors">
                    {text}
                  </span>
                </div>
              ))}
            </div>

            {/* Trust line */}
            <p className="text-slate-600 text-xs font-bold uppercase tracking-widest flex items-center gap-3">
              <span className="w-1 h-1 rounded-full bg-green-500 inline-block" />
              Sin costos de inscripción
              <span className="w-1 h-1 rounded-full bg-green-500 inline-block" />
              Retiros automáticos
            </p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default RegistroVendedor;
